import {
  getCatalogRamSnapshotCache,
  type WebCatalogCategoryRow,
} from './catalogRamSnapshotCache';

type WebCatalogProductRow = NonNullable<ReturnType<typeof getCatalogRamSnapshotCache>>['products'][number];
import type { CategoryRow } from './categoryRowTypes';
/** Fila de producto para mutaciones (forma compatible con queries SQLite). */
export type ProductMutationRow = {
  id: string;
  is_active: number | null;
  barcode: string | null;
  name: string;
  price: number;
  sku: string;
  category_id: string | null;
  organization_id: string | null;
  item_type: string | null;
  is_misc: number | null;
  brand_id: string | null;
  internal_notes: string | null;
  booking_mode?: string | null;
  evaluation_product_id?: string | null;
  booking_payment_mode?: string | null;
  booking_deposit_amount?: number | null;
  booking_deposit_percent?: number | null;
  preferred_supplier_id: string | null;
  supplier_item_code: string | null;
  tax_class: string | null;
  unit_of_measure: string | null;
  sell_by_weight: number | null;
  duration_minutes: number | null;
  buffer_before_minutes: number | null;
  buffer_after_minutes: number | null;
  image_url: string | null;
  video_url: string | null;
  public_description: string | null;
  is_public: number | null;
  active_ingredient: string | null;
  usage_tags: string | null;
  kitchen_destination: string | null;
  prep_minutes: number | null;
  menu_available: number | null;
  parent_product_id: string | null;
};

export function mapWebProductToMutationRow(row: WebCatalogProductRow): ProductMutationRow {
  return {
    id: String(row.id),
    is_active: row.is_active !== false ? 1 : 0,
    barcode: row.barcode != null ? String(row.barcode) : null,
    name: String(row.name ?? ''),
    price: Number(row.price ?? 0),
    sku: row.sku != null ? String(row.sku) : '',
    category_id: row.category_id != null ? String(row.category_id) : null,
    organization_id: row.organization_id != null ? String(row.organization_id) : null,
    item_type: row.item_type === 'service' ? 'service' : 'physical',
    is_misc: row.is_misc === true ? 1 : 0,
    brand_id: row.brand_id != null ? String(row.brand_id) : null,
    internal_notes: row.internal_notes != null ? String(row.internal_notes) : null,
    booking_mode: row.booking_mode ?? null,
    evaluation_product_id: row.evaluation_product_id ?? null,
    booking_payment_mode: row.booking_payment_mode ?? null,
    booking_deposit_amount: row.booking_deposit_amount ?? null,
    booking_deposit_percent: row.booking_deposit_percent ?? null,
    preferred_supplier_id: row.preferred_supplier_id != null ? String(row.preferred_supplier_id) : null,
    supplier_item_code: row.supplier_item_code != null ? String(row.supplier_item_code) : null,
    tax_class: row.tax_class != null ? String(row.tax_class) : null,
    unit_of_measure: row.unit_of_measure != null ? String(row.unit_of_measure) : null,
    sell_by_weight: row.sell_by_weight ? 1 : 0,
    duration_minutes: row.duration_minutes ?? null,
    buffer_before_minutes: row.buffer_before_minutes ?? null,
    buffer_after_minutes: row.buffer_after_minutes ?? null,
    image_url: row.image_url ?? null,
    video_url: row.video_url ?? null,
    public_description: row.public_description ?? null,
    is_public: row.is_public !== false ? 1 : 0,
    active_ingredient: row.active_ingredient ?? null,
    usage_tags: Array.isArray(row.usage_tags) ? JSON.stringify(row.usage_tags) : '[]',
    kitchen_destination: row.kitchen_destination ?? 'kitchen',
    prep_minutes: row.prep_minutes != null ? Number(row.prep_minutes) : null,
    menu_available: row.menu_available === false ? 0 : 1,
    parent_product_id: row.parent_product_id ?? null,
  };
}

export function mapWebCategoryToCategoryRow(row: WebCatalogCategoryRow): CategoryRow {
  return {
    id: String(row.id),
    organization_id: row.organization_id != null ? String(row.organization_id) : null,
    slug: String(row.slug ?? ''),
    name: String(row.name ?? ''),
    is_active: row.is_active !== false,
    is_restricted: row.is_restricted === true,
    sort_order: row.sort_order ?? 0,
    color: row.color ?? null,
    applies_to: row.applies_to ?? null,
    parent_id: row.parent_id ?? null,
    code: row.code ?? null,
    default_margin_percent: row.default_margin_percent ?? null,
    description: row.description ?? null,
    image_url: row.image_url ?? null,
    created_at: row.created_at ?? null,
    updated_at: row.updated_at ?? null,
    deleted_at: row.deleted_at ?? null,
  };
}

export function resolveProductRowFromWebCache(productId: string): ProductMutationRow | null {
  const cached = getCatalogRamSnapshotCache()?.products.find((p) => String(p.id) === productId);
  return cached ? mapWebProductToMutationRow(cached) : null;
}

export function resolveCategoryRowFromWebCache(
  categoryId: string,
  organizationId?: string | null,
): CategoryRow | null {
  const rows = getCatalogRamSnapshotCache()?.categories ?? [];
  const match = rows.find((row) => {
    if (!row?.id || row.deleted_at) return false;
    if (organizationId && String(row.organization_id ?? '') !== organizationId) return false;
    return String(row.id) === categoryId;
  });
  return match ? mapWebCategoryToCategoryRow(match) : null;
}

export function listCategoryChildRowsFromWebCache(
  organizationId: string,
  parentId: string,
): CategoryRow[] {
  return (getCatalogRamSnapshotCache()?.categories ?? [])
    .filter(
      (row) =>
        row?.id &&
        !row.deleted_at &&
        String(row.organization_id ?? '') === organizationId &&
        String(row.parent_id ?? '') === parentId,
    )
    .map(mapWebCategoryToCategoryRow);
}

export function sumProductStockQuantityFromWebCache(productId: string): number {
  return (getCatalogRamSnapshotCache()?.stocks ?? [])
    .filter((s) => String(s.product_id) === productId)
    .reduce((sum, s) => sum + Number(s.quantity ?? 0), 0);
}

const PRODUCT_MUTATION_SELECT = `SELECT id, is_active, barcode, name, price, sku, category_id, organization_id, item_type, is_misc,
            brand_id, internal_notes, preferred_supplier_id, supplier_item_code, tax_class,
            unit_of_measure, sell_by_weight, duration_minutes, buffer_before_minutes, buffer_after_minutes,
            image_url, video_url, public_description, is_public, active_ingredient, usage_tags,
            booking_mode, evaluation_product_id, booking_payment_mode,
            booking_deposit_amount, booking_deposit_percent,
            COALESCE(kitchen_destination, 'kitchen') as kitchen_destination,
            COALESCE(menu_available, 1) as menu_available,
            parent_product_id
     FROM products WHERE id = ?`;

export async function resolveProductRowForMutation(productId: string): Promise<ProductMutationRow | null> {
  return resolveProductRowFromWebCache(productId);
}

const PRODUCT_LIFECYCLE_SELECT = `SELECT id, name, price, sku, barcode, category_id, is_active, organization_id, item_type, is_misc FROM products WHERE id = ?`;

export async function resolveProductLifecycleRowForMutation(productId: string): Promise<{
  id: string;
  name: string;
  price: number;
  sku: string;
  barcode: string | null;
  category_id: string | null;
  is_active: number | null;
  organization_id: string | null;
  item_type: string | null;
  is_misc: number | null;
} | null> {
  const row = resolveProductRowFromWebCache(productId);
        if (!row) return null;
        return {
          id: row.id,
          name: row.name,
          price: row.price,
          sku: row.sku,
          barcode: row.barcode,
          category_id: row.category_id,
          is_active: row.is_active,
          organization_id: row.organization_id,
          item_type: row.item_type,
          is_misc: row.is_misc,
        };
}
